/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Link } from '@/components/link';
import { Button } from '@/components/ui/button';
import { routes } from '@/route-paths';
import { useTranslation } from 'react-i18next';
import styles from './signup-verification-sent.module.css';

type SignupVerificationSentProps = {
    email: string;
};

function maskEmail(email: string): string {
    const [localPart, domain] = email.split('@');

    if (!localPart || !domain) {
        return '';
    }

    const visibleCharacterCount = localPart.length > 2 ? 2 : 1;
    const visibleLocalPart = localPart.slice(0, visibleCharacterCount);
    const maskedCharacterCount = Math.max(3, localPart.length - visibleCharacterCount);

    return `${visibleLocalPart}${'•'.repeat(maskedCharacterCount)}@${domain}`;
}

function EmailSentAnimation() {
    return (
        <div className={styles.animationStage} aria-hidden="true">
            <svg className={styles.animation} viewBox="0 0 128 112" role="presentation">
                <g className={styles.envelope}>
                    <rect x="28" y="34" width="72" height="50" rx="4" className={styles.envelopeBody} />
                    <path d="M31 39 64 63 97 39" className={styles.envelopeFold} />
                    <path d="m30 81 25-22M98 81 73 59" className={styles.envelopeSeam} />
                </g>
                <g className={styles.motionLines}>
                    <path d="M42 91h44" />
                    <path d="M50 100h28" />
                </g>
                <circle cx="64" cy="54" r="25" className={styles.checkCircle} />
                <path d="m52 54 8 8 17-18" className={styles.checkMark} />
            </svg>
        </div>
    );
}

export function SignupVerificationSent({ email }: SignupVerificationSentProps) {
    const { t } = useTranslation('signup');
    const maskedEmail = maskEmail(email);

    return (
        <section
            data-section="auth"
            className={`section-container py-10 sm:py-14 lg:py-16 ${styles.page}`}
            aria-labelledby="verification-sent-title"
            aria-describedby="verification-sent-message">
            <div className="mx-auto max-w-xl text-center">
                <EmailSentAnimation />

                <h1
                    id="verification-sent-title"
                    className="mt-6 text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
                    {t('verificationSent.title')}
                </h1>

                <div id="verification-sent-message" className="mx-auto mt-5 max-w-md space-y-3">
                    <p className="text-base leading-7 text-muted-foreground">{t('verificationSent.message')}</p>
                    {maskedEmail ? <p className="font-semibold text-foreground">{maskedEmail}</p> : null}
                    <p className="text-base leading-7 text-muted-foreground">
                        {t('verificationSent.activationInstruction')}
                    </p>
                    <p className="text-sm text-muted-foreground">{t('verificationSent.spamReminder')}</p>
                </div>

                <div className={`mx-auto mt-8 ${styles.actions}`}>
                    <Button asChild size="lg" className="h-12 w-full text-base">
                        <Link to={routes.login}>{t('verificationSent.cta')}</Link>
                    </Button>
                    <Link
                        to={routes.home}
                        className="mx-auto rounded-ui px-2 py-1 text-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                        {t('verificationSent.continueShopping')}
                    </Link>
                </div>
            </div>
        </section>
    );
}
